// src/pages/PartnerSignup.tsx
'use client';
import React from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import Input from "@/components/Input";
import Button from "@/components/Button";
import ProgressBar from "@/components/ProgressBar";
import { ENV } from "@/lib/env";

function isEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function PartnerSignup() {
  const [email, setEmail] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);
  const nav = useNavigate();

  const sendCode = async () => {
    setErr(null);
    if (!isEmail(email)) {
      setErr("Please enter a valid email address.");
      return;
    }
    if (!ENV.SUPABASE_URL) {
      setErr("App is not configured.");
      return;
    }

    try {
      setSending(true);
      const res = await fetch(`${ENV.EDGE_BASE}/auth-email-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ENV.SUPABASE_ANON}`,
        },
        body: JSON.stringify({ email, partner: true }),
      });

      const text = await res.text();
      let json: any = {};
      try { json = JSON.parse(text); } catch {}

      if (!res.ok) throw new Error(json?.error || "Failed to send code");

      localStorage.setItem("partner_signup_email", email);
      nav("/partner/check-email");
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0f172a]">
            Become a Partner
          </h1>
          <p className="text-sm sm:text-base text-gray-500 mt-1">
            Earn 20% recurring revenue. Get your branded AI coach.
          </p>
        </div>

        {/* EMAIL INPUT */}
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            Business Email
          </label>
          <Input
            type="email"
            placeholder="gym@alpha.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-gray-800 border-gray-700 text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:ring-[#a78bfa]"
          />
        </div>

        {/* ERROR */}
        {err && (
          <p className="text-sm text-red-400 text-center">{err}</p>
        )}

        {/* SEND BUTTON */}
        <Button
          onClick={sendCode}
          disabled={sending}
          className={`
            w-full bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] font-semibold
            rounded-2xl py-3 transition-all duration-200
            active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed
          `}
        >
          {sending ? "Sending Code..." : "Send Login Code"}
        </Button>

        {/* FOOTER LINKS */}
        <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
          <a href="#" className="hover:text-[#0f172a] transition">Terms</a>
          <a href="#" className="hover:text-[#0f172a] transition">Privacy</a>
          <a href="#" className="hover:text-[#0f172a] transition">Support</a>
        </div>

        <div className="my-6 border-t border-gray-800" />

        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <span className="w-2 h-2 bg-[#a78bfa] rounded-full" />
          Input
        </div>

        <footer className="text-center text-xs text-gray-500">
          © 2025 Input. All rights reserved.
        </footer>

        {/* PROGRESS BAR (same as client flow) */}
        <div className="mt-6">
          <ProgressBar step={1} total={8} />
        </div>
      </div>
    </AuthLayout>
  );
}
